import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { SocialHistoryCodeRow } from '@/types/patient-chart-api';

/**
 * Master data — Social-history-type typeahead for the Social History
 * Add flow (PROD-827 chart write surfaces).
 *
 * Wire (curl-verified by Andrew 2026-05-20):
 *
 *   GET /api/Master/GetSocialHistoryList?searchQuery={query}
 *       → SocialHistoryCodeRow[]
 *
 * Each row is a Coding `{code, display, system}` extended with an
 * OPTIONAL nested `category` Coding. Drug/alcohol/etc. categories
 * surface with the category nested; the CreateSocialHistory wire
 * body ships the row VERBATIM (including the nested category) — the
 * server normalises the shape on round-trip (Update strips the nesting
 * and surfaces `category` at top level instead).
 *
 * Stale-request safety: same four-layer defense as the other master
 * search hooks (allergies / diagnosis / procedures).
 */

const MIN_QUERY_LENGTH = 1;

export function useSocialHistoryCodeSearch(query: string, enabled = true) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ['master-social-history-codes', trimmed],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ searchQuery: trimmed });
      return get<SocialHistoryCodeRow[]>(
        `/api/Master/GetSocialHistoryList?${qs.toString()}`,
        { signal },
      );
    },
    enabled: enabled && trimmed.length >= MIN_QUERY_LENGTH,
    staleTime: 5 * 60 * 1000,
    placeholderData: (prev) => prev,
  });
}
